import {
  Box,
  Button,
  Flex,
  Text,
  ButtonGroup,
  Grid,
  GridItem,
  Textarea,
  Input,
  Badge,
  Spacer,
  Divider,
} from "@chakra-ui/react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { DocumentViewer } from "../../Components/common/DocumentViewer";
import isAllowed from "../../routing/isAllowed";
import { useNavigate } from "react-router-dom";
import {
  approveApplication,
  getIncomingApplicationById,
  postAssignToMe,
  rejectApplication,
} from "./ContractorSlice";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import DOCUMENT_FROM from "../../Common/Constants";

function RegistrationCertificateDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [status, setStatus] = useState("idle");
  const { application } = useSelector((state) => state.contractor);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const applicationRenewStatus = "old";

  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const getList = async (id) => {
    try {
      setStatus("pending");
      setLoadError(null);
      const result = await dispatch(getIncomingApplicationById(id));
      if (result?.payload) {
        setStatus("success");
      } else if (result?.error) {
        setLoadError("Failed to load application. Please try again.");
        setStatus("failed");
      }
    } catch (error) {
      setLoadError(
        "Failed to load application. Token may have expired. Please refresh the page."
      );
      setStatus("failed");
      console.error(error);
    }
  };

  const handleRetry = () => {
    setRetrying(true);
    getList(encodeURIComponent(id));
    setTimeout(() => setRetrying(false), 1000);
  };

  const assignToMe = async (id) => {
    try {
      setStatus("pending");
      const result = await dispatch(
        postAssignToMe({ id, applicationRenewStatus })
      );
      if (result.meta.requestStatus === "fulfilled") {
        setStatus("success");
        setTimeout(() => {
          navigate(-1);
        }, 500);
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error(error, "error");
      setStatus("failed");
    }
  };

  const approveApplicationForm = (id) => {
    setIsLoading(true);
    try {
      setStatus("pending");
      const result = dispatch(approveApplication(id));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const rejectApplicationForm = (id) => {
    setIsLoading(true);
    try {
      setStatus("pending");
      const result = dispatch(rejectApplication(id));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
    onClose();
  };

  const [isOpen, setIsOpen] = useState(false);

  const onClose = () => setIsOpen(false);

  useEffect(() => {
    getList(encodeURIComponent(id));
  }, [dispatch, id]);

  return (
    <Box
      marginBottom={"2rem"}
      padding={{ base: "1rem", md: "2rem", lg: "3rem" }}
      maxW="100%"
      overflowX="hidden"
    >
      {/* Error Message Display */}
      {loadError && status === "failed" && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="md"
          p="4"
          mb="4"
          color="red.800"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text>{loadError}</Text>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={handleRetry}
              isLoading={retrying}
            >
              Retry
            </Button>
          </Flex>
        </Box>
      )}
      <Box
        display={"flex"}
        alignItems="center"
        cursor={"pointer"}
        onClick={handleGoBack}
      >
        <IoChevronBackCircleSharp color={"#52BBFF"} size={30} />
        &nbsp;
        <Text
          color={"#52BBFF"}
          fontSize={{ base: "sm", md: "sm", lg: "md" }}
          fontWeight={"800"}
          fontFamily={"font.roboto"}
        >
          Go Back
        </Text>
      </Box>

      <Box marginTop="2rem">
        <Text
          color={"#141D43"}
          fontSize="2xl"
          fontWeight={"800"}
          fontFamily={"font.roboto"}
          wordBreak="break-word"
        >
          Registration Certificate
        </Text>
      </Box>

      <Box
        marginTop={"1.5rem"}
        bg={
          "linear-gradient(90deg, rgba(81,187,255,1) 0%, rgba(81,187,255,1) 15%, rgba(164,206,255,1) 100%)"
        }
        color={"#fff"}
        padding={"10px 2rem"}
        fontWeight="700"
        marginLeft={{ base: "-1rem", md: "-2rem", lg: "-5rem" }}
        marginRight={{ base: "-1rem", md: "-2rem", lg: "-5rem" }}
        borderTopRightRadius="100px"
        borderBottomRightRadius={"80px 80px"}
        wordBreak="break-word"
        overflowWrap="break-word"
      >
        Application # {application?.id}
      </Box>

      {/* ── Applicant Details Card ── */}
      <Box
        bg="#fff"
        padding={{ base: "1rem", md: "2rem" }}
        marginTop="2rem"
        borderRadius={"15px"}
        overflow="hidden"
        maxW="100%"
      >
        <Text color={"#141D43"} fontSize="lg" fontWeight={"800"}>
          <Box
            as="span"
            borderRight={"3px solid #2272D5"}
            height="40px"
            marginRight={"1rem"}
          ></Box>
          Applicant Details
        </Text>

        <Box paddingLeft={{ base: "0", md: "1rem", lg: "3rem" }}>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={{ base: "1rem", md: "2rem" }}
            marginTop={"1rem"}
          >
            <Box display={"flex"} flexDirection="column" gap="1.5rem">
              {/* Name of Organisation */}
              <Box>
                <Text
                  color={"#141D43"}
                  fontSize="md"
                  fontWeight={"700"}
                  fontFamily={"font.roboto"}
                >
                  Name of Organisation
                </Text>
                <Text
                  color={"#464C64"}
                  fontSize="sm"
                  marginTop={"0.5rem"}
                  wordBreak="break-word"
                  overflowWrap="break-word"
                >
                  {application?.name ? application?.name : "N/A"}
                </Text>
              </Box>

              {/* Address */}
              <Box>
                <Text
                  color={"#141D43"}
                  fontSize="md"
                  fontWeight={"700"}
                  fontFamily={"font.roboto"}
                >
                  Address
                </Text>
                <Text
                  color={"#464C64"}
                  fontSize="sm"
                  marginTop={"0.5rem"}
                  wordBreak="break-word"
                  overflowWrap="break-word"
                >
                  {application?.address?.raw_address
                    ? application?.address?.raw_address
                    : "N/A"}
                </Text>
              </Box>

              {/* City & State */}
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={{ base: "1rem", md: "2rem" }}
              >
                <GridItem>
                  <Text
                    color={"#141D43"}
                    fontSize="md"
                    fontWeight={"700"}
                    fontFamily={"font.roboto"}
                  >
                    City
                  </Text>
                  <Text
                    color={"#464C64"}
                    fontSize="sm"
                    marginTop={"0.5rem"}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {application?.address?.city
                      ? application?.address?.city
                      : "N/A"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    color={"#141D43"}
                    fontSize="md"
                    fontWeight={"700"}
                    fontFamily={"font.roboto"}
                  >
                    State
                  </Text>
                  <Text
                    color={"#464C64"}
                    fontSize="sm"
                    marginTop={"0.5rem"}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {application?.address?.state
                      ? application?.address?.state
                      : "N/A"}
                  </Text>
                </GridItem>
              </Grid>

              {/* Pincode & Country */}
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={{ base: "1rem", md: "2rem" }}
              >
                <GridItem>
                  <Text
                    color={"#141D43"}
                    fontSize="md"
                    fontWeight={"700"}
                    fontFamily={"font.roboto"}
                  >
                    Pincode
                  </Text>
                  <Text
                    color={"#464C64"}
                    fontSize="sm"
                    marginTop={"0.5rem"}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {application?.address?.pinCode
                      ? application?.address?.pinCode
                      : "N/A"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    color={"#141D43"}
                    fontSize="md"
                    fontWeight={"700"}
                    fontFamily={"font.roboto"}
                  >
                    Country
                  </Text>
                  <Text
                    color={"#464C64"}
                    fontSize="sm"
                    marginTop={"0.5rem"}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {application?.address?.country
                      ? application?.address?.country
                      : "N/A"}
                  </Text>
                </GridItem>
              </Grid>
            </Box>
          </Grid>
        </Box>

        {/* Legal Representative */}
        <Text
          color={"#141D43"}
          fontSize="lg"
          fontWeight={"800"}
          marginBottom="5px"
          marginTop="1.5rem"
        >
          Legal Representative
        </Text>
        <Box
          border={"2px solid #2272D5"}
          width="10%"
          marginBottom={"1rem"}
        ></Box>
        <Box paddingLeft={{ base: "0", md: "1rem", lg: "3rem" }}>
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={{ base: "1rem", md: "1.5rem" }}
            marginTop={"1rem"}
          >
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Name
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.legalRepresentative?.name
                  ? application?.legalRepresentative?.name
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Email
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.legalRepresentative?.email
                  ? application?.legalRepresentative?.email
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Contact
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.legalRepresentative?.contact
                  ? application.legalRepresentative.contact
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>
        </Box>

        {/* Point of Contact */}
        <Text
          color={"#141D43"}
          fontSize="lg"
          fontWeight={"800"}
          marginBottom="5px"
          marginTop="1.5rem"
        >
          Point of Contact
        </Text>
        <Box
          border={"2px solid #2272D5"}
          width="10%"
          marginBottom={"1rem"}
        ></Box>
        <Box paddingLeft={{ base: "0", md: "1rem", lg: "3rem" }}>
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={{ base: "1rem", md: "1.5rem" }}
            marginTop={"1rem"}
          >
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Name
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.contactPerson?.name
                  ? application?.contactPerson?.name
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Email
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.contactPerson?.email
                  ? application?.contactPerson?.email
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Contact
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.contactPerson?.contact
                  ? application?.contactPerson?.contact
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>

          {/* Organisation Email & Contact Number */}
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
            gap={{ base: "1rem", md: "2rem" }}
            marginTop={"1.5rem"}
          >
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Organization Email
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.contactPerson?.email
                  ? application?.contactPerson?.email
                  : "N/A"}
              </Text>
            </GridItem>
            <GridItem>
              <Text
                color={"#141D43"}
                fontSize="md"
                fontWeight={"700"}
                fontFamily={"font.roboto"}
              >
                Contact Number
              </Text>
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {application?.contactPerson?.contact
                  ? application?.contactPerson?.contact
                  : "N/A"}
              </Text>
            </GridItem>
          </Grid>

          {/* Website */}
          <Box marginTop={"1.5rem"}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Website/Url
            </Text>
            <a
              href={`${application?.website}`}
              target="_blank"
              rel="noreferrer"
            >
              <Text
                color={"#464C64"}
                fontSize="sm"
                marginTop={"0.5rem"}
                wordBreak="break-all"
                overflowWrap="anywhere"
              >
                {application?.website ? application?.website : "N/A"}
              </Text>
            </a>
          </Box>

          {/* Description */}
          <Box marginTop={"1.5rem"}>
            <Text
              color={"#141D43"}
              fontSize="md"
              fontWeight={"700"}
              fontFamily={"font.roboto"}
            >
              Brief Description about Organisation
            </Text>
            <Text
              color={"#464C64"}
              fontSize="sm"
              marginTop={"0.5rem"}
              wordBreak="break-word"
              overflowWrap="break-word"
            >
              {application?.description ? application?.description : "N/A"}
            </Text>
          </Box>
        </Box>
      </Box>

      {/* ── Applicant Details and Scope Card ── */}
      <Box
        bg="#fff"
        padding={{ base: "1rem", md: "2rem" }}
        marginTop="2rem"
        borderRadius={"15px"}
        overflow="hidden"
        maxW="100%"
      >
        <Text color={"#141D43"} fontSize="lg" fontWeight={"800"}>
          <Box
            as="span"
            borderRight={"3px solid #2272D5"}
            height="40px"
            marginRight={"1rem"}
          ></Box>
          Applicant Details and scope
        </Text>

        {application?.units?.length > 0 ? (
          <Box>
            {application?.units?.map((unit, index) => (
              <Box
                key={index}
                paddingLeft={{ base: "0", md: "1rem", lg: "3rem" }}
              >
                <Box
                  display={"flex"}
                  flexDirection="column"
                  gap="1.5rem"
                  marginTop={"1rem"}
                >
                  {/* Facility Name & Number of Employees */}
                  <Grid
                    templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                    gap={{ base: "1rem", md: "2rem" }}
                  >
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Facility/Site Name
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.name ? unit.name : "N/A"}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Number Of Employees
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.numberOfEmployees
                          ? unit?.numberOfEmployees
                          : "N/A"}
                      </Text>
                    </GridItem>
                  </Grid>

                  {/* Address */}
                  <Box>
                    <Text
                      color={"#141D43"}
                      fontSize="md"
                      fontWeight={"700"}
                      fontFamily={"font.roboto"}
                    >
                      Address
                    </Text>
                    <Text
                      color={"#464C64"}
                      fontSize="sm"
                      marginTop={"0.5rem"}
                      wordBreak="break-word"
                      overflowWrap="break-word"
                    >
                      {unit?.address?.raw_address
                        ? unit?.address?.raw_address
                        : "N/A"}
                    </Text>
                  </Box>

                  {/* City & State */}
                  <Grid
                    templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                    gap={{ base: "1rem", md: "2rem" }}
                  >
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        City
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.address?.city ? unit?.address?.city : "N/A"}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        State
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.address?.state ? unit?.address?.state : "N/A"}
                      </Text>
                    </GridItem>
                  </Grid>

                  {/* Pincode & Country */}
                  <Grid
                    templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                    gap={{ base: "1rem", md: "2rem" }}
                  >
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Pincode
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.address?.pinCode
                          ? unit?.address?.pinCode
                          : "N/A"}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text
                        color={"#141D43"}
                        fontSize="md"
                        fontWeight={"700"}
                        fontFamily={"font.roboto"}
                      >
                        Country
                      </Text>
                      <Text
                        color={"#464C64"}
                        fontSize="sm"
                        marginTop={"0.5rem"}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        {unit?.address?.country
                          ? unit?.address?.country
                          : "N/A"}
                      </Text>
                    </GridItem>
                  </Grid>

                  {/* Type of organisation */}
                  <Box>
                    <Text
                      color={"#141D43"}
                      fontSize="md"
                      fontWeight={"700"}
                      fontFamily={"font.roboto"}
                    >
                      Type of organization
                    </Text>
                    <Text
                      color={"#464C64"}
                      fontSize="sm"
                      marginTop={"0.5rem"}
                      wordBreak="break-word"
                      overflowWrap="break-word"
                    >
                      {unit?.unitType ? unit.unitType : "N/A"}
                    </Text>
                  </Box>

                  {/* Type of Process — Badges */}
                  <Box>
                    <Text
                      color={"#141D43"}
                      fontSize="md"
                      fontWeight={"700"}
                      fontFamily={"font.roboto"}
                    >
                      Type of Process
                    </Text>
                    {unit?.processIds?.length > 0 ? (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap="0.5rem"
                        marginTop={"0.75rem"}
                      >
                        {unit?.processIds?.map((name, i) => (
                          <Badge
                            key={i}
                            fontSize={{ base: "xs", md: "sm" }}
                            bg={"#EDF3F8"}
                            color="#141D43"
                            cursor={"pointer"}
                            whiteSpace="normal"
                            wordBreak="break-word"
                            px="0.5rem"
                            py="0.25rem"
                          >
                            {name}
                          </Badge>
                        ))}
                      </Box>
                    ) : (
                      <Text mt="0.5rem">No processes available</Text>
                    )}
                  </Box>

                  {/* Selected Programs — Badges */}
                  <Box>
                    <Text
                      color={"#141D43"}
                      fontSize="md"
                      fontWeight={"700"}
                      fontFamily={"font.roboto"}
                    >
                      Selected Programs
                    </Text>
                    {unit?.programIds?.length > 0 ? (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap="0.5rem"
                        marginTop={"0.75rem"}
                      >
                        {unit?.programIds?.map((name, i) => (
                          <Badge
                            key={i}
                            fontSize={{ base: "xs", md: "md" }}
                            bg={"#EDF3F8"}
                            color="#141D43"
                            cursor={"pointer"}
                            whiteSpace="normal"
                            wordBreak="break-word"
                            px="0.5rem"
                            py="0.25rem"
                          >
                            {name}
                          </Badge>
                        ))}
                      </Box>
                    ) : (
                      <Text mt="0.5rem">No programs available</Text>
                    )}
                  </Box>
                </Box>
                <Box w={"100%"} h={"1px"} bg="gray" mt="1.5rem" mb="1rem"></Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            mt="2rem"
            p={"1rem"}
            boxShadow={"rgba(100, 100, 111, 0.2) 0px 2px 10px 0px"}
          >
            No details available
          </Box>
        )}
      </Box>

      {/* ── RC Documents Card ── */}
      <Box
        marginTop="2rem"
        bg="#FFFFFF"
        borderRadius={"15px"}
        p={{ base: "1rem", md: "2rem" }}
        overflow="hidden"
      >
        <Text color={"#141D43"} fontSize="lg" fontWeight={"800"} pb="1rem">
          RC Documents
        </Text>
        {application?.application_documentsForVerification?.length > 0 ? (
          <DocumentViewer
            document={application?.application_documentsForVerification}
            referenceId={application?._id}
            doc_from={DOCUMENT_FROM.APPLICATION}
          />
        ) : (
          <Box
            bg="#FFFFFF"
            w={"175px"}
            h={"125px"}
            borderRadius={"15px"}
            textAlign="center"
            display="flex"
            alignItems="center"
            mb="1rem"
            boxShadow={"rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
          >
            <Text padding={"1rem"}>No Application Document Found</Text>
          </Box>
        )}
      </Box>

      {/* ── Product Details Card ── */}
      <Box
        bg="#fff"
        borderRadius={"15px"}
        padding={{ base: "1rem", md: "2rem", lg: "3rem" }}
        marginTop="2rem"
        overflow="hidden"
        maxW="100%"
      >
        <Text color={"#141D43"} fontSize="lg" fontWeight={"800"}>
          <Box
            as="span"
            borderRight={"3px solid #2272D5"}
            height="40px"
            marginRight={"1rem"}
          ></Box>
          Product Details
        </Text>

        {application?.units?.length > 0 ? (
          <>
            {/* Header row */}
            <Grid
              mt={{ base: "1rem", md: "2rem" }}
              templateColumns={{ base: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
              rowGap={{ base: "0.5rem", md: "1rem" }}
              columnGap={{ base: "0.5rem", md: "1rem" }}
              padding={{ base: "0.5rem", md: "1rem" }}
            >
              <GridItem>
                <Text
                  fontWeight={"700"}
                  fontSize={{ base: "sm", md: "md" }}
                  wordBreak="break-word"
                >
                  Product Name
                </Text>
              </GridItem>
              <GridItem>
                <Text
                  fontWeight={"700"}
                  fontSize={{ base: "sm", md: "md" }}
                  wordBreak="break-word"
                >
                  Product Category
                </Text>
              </GridItem>
              <GridItem>
                <Text
                  fontWeight={"700"}
                  fontSize={{ base: "sm", md: "md" }}
                  wordBreak="break-word"
                >
                  Product Classification
                </Text>
              </GridItem>
              <GridItem>
                <Text
                  fontWeight={"700"}
                  fontSize={{ base: "sm", md: "md" }}
                  wordBreak="break-word"
                >
                  Product Composition
                </Text>
              </GridItem>
            </Grid>

            {application?.products?.map((product, index) => (
              <Grid
                key={index}
                padding={{ base: "0.5rem", md: "1rem" }}
                templateColumns={{
                  base: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                }}
                rowGap={{ base: "0.5rem", md: "1rem" }}
                columnGap={{ base: "0.5rem", md: "1rem" }}
              >
                <GridItem>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {product?.name ? product?.name : "N/A"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {product?.productCategory?.name
                      ? product?.productCategory?.name
                      : "N/A"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {product?.productClassification?.name
                      ? product?.productClassification?.name
                      : "N/A"}
                  </Text>
                </GridItem>
                <GridItem>
                  {product?.certifiedLabelGrade?.commodities?.length > 0 ? (
                    <Box fontWeight="bold">
                      {product?.certifiedLabelGrade?.commodities.map(
                        (commodity) => (
                          <Text
                            key={commodity.id}
                            fontSize={{ base: "sm", md: "md" }}
                            wordBreak="break-word"
                            overflowWrap="break-word"
                          >
                            {commodity.commodityId.name},
                          </Text>
                        )
                      )}
                    </Box>
                  ) : (
                    <Text fontSize={{ base: "sm", md: "md" }}>N/A</Text>
                  )}
                  {product?.ncProductComposition?.length > 0 ? (
                    <Box display="flex" flexWrap="wrap">
                      {product?.ncProductComposition?.map((nc) => (
                        <Text
                          key={nc._id}
                          fontSize={{ base: "sm", md: "md" }}
                          wordBreak="break-word"
                          overflowWrap="break-word"
                        >
                          {nc.commodity.name},
                        </Text>
                      ))}
                    </Box>
                  ) : null}
                </GridItem>
              </Grid>
            ))}
          </>
        ) : (
          <Box mt="1rem" p={"1rem"}>
            No product details available
          </Box>
        )}
      </Box>

      {/* ── Action Buttons ── */}
      <Box mt={"2rem"}>
        <ButtonGroup gap={2} flexWrap="wrap">
          <Button
            bg="#A8BF87"
            color="brand.white"
            w={{ base: "100%", md: "193px" }}
            h="50px"
            disabled={isLoading ? true : ""}
            onClick={() => assignToMe(encodeURIComponent(id))}
          >
            Assign to me
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default isAllowed(RegistrationCertificateDetails);
